// handlewebsocketconnection.js

import { useRef, useEffect } from 'react';
import MessageHeaderDecoder from '../aeron/MessageHeaderDecoder.js';

const useWebSocketConnection = (url, handleIncomingMessage) => {
    console.debug('In useWebSocketConnection');
    const socketRef = useRef(null);
    const pingIntervalRef = useRef(null);

    useEffect(() => {
        console.log('WebSocketProvider mounted');

        const connect = () => {
            console.log('Connecting to WebSocket...', url, Math.random());
            const socket = new WebSocket(url);
            socket.binaryType = 'arraybuffer'; // Ensure the socket handles binary data
            socketRef.current = socket;

            socket.onopen = () => {
                console.log('WebSocket connection opened');
                startPing();
                socket.send(JSON.stringify({ type: 'welcome', message: 'Connection established' }));
            };

            socket.onmessage = (event) => {
                if (event.data instanceof ArrayBuffer) {
                    logData(event.data);
                    handleIncomingMessage(event.data);
                } else if (typeof event.data === 'string') {
                    const captureMessage = parseCaptureMessage(event.data);
                    if (captureMessage) {
                        logData(captureMessage.payload);
                        handleIncomingMessage(captureMessage.payload, captureMessage.metadata);
                        return;
                    }

                    console.log('Received:', event.data);
                } else {
                    console.log('Received:', event.data);
                }
            };

            socket.onclose = (event) => {
                console.log('WebSocket connection closed', {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                });
                stopPing();
                if (event.code !== 1000) { // Only reconnect if the close was not clean
                    setTimeout(connect, 5000); // Reconnect after 5 seconds
                }
            };

            socket.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
        };

        const startPing = () => {
            pingIntervalRef.current = setInterval(() => {
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                    socketRef.current.send(JSON.stringify({ type: 'ping' }));
                }
            }, 30000); // Ping every 30 seconds
        };

        const stopPing = () => {
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
        };

        connect();

        return () => {
            console.log('WebSocketProvider unmounted');
            if (socketRef.current) {
                socketRef.current.close(1000, 'Component unmounted'); // Cleanly close the WebSocket
            }
            stopPing();
        };
    }, [url, handleIncomingMessage]);

    console.debug('Out useWebSocketConnection');
    return socketRef;
};

export default useWebSocketConnection;

function parseCaptureMessage(rawMessage) {
    try {
        const parsed = JSON.parse(rawMessage);
        if (parsed?.type !== 'capture' || !parsed.payloadBase64) {
            return null;
        }

        return {
            payload: decodeBase64ToArrayBuffer(parsed.payloadBase64),
            metadata: {
                channelName: parsed.channelName,
                sourceChannel: parsed.sourceChannel,
                streamId: parsed.streamId,
                sessionId: parsed.sessionId,
                sourcePosition: parsed.sourcePosition,
                ingestSource: parsed.ingestSource,
                ingestTimestamp: parsed.ingestTimestamp,
            },
        };
    } catch (error) {
        return null;
    }
}

function decodeBase64ToArrayBuffer(base64) {
    if (typeof Buffer !== 'undefined') {
        const buffer = Buffer.from(base64, 'base64');
        return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
    }

    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let index = 0; index < binaryString.length; index += 1) {
        bytes[index] = binaryString.charCodeAt(index);
    }
    return bytes.buffer;
}

function logData(data) {
    // 🧪 LOG: check alignment after header
    const startOffset = MessageHeaderDecoder.ENCODED_LENGTH;
    const byteSlice = new Uint8Array(data, startOffset);
    const hexDump = Array.from(byteSlice).map(b => b.toString(16).padStart(2, '0')).join(' ');
    console.log('🧪 Bytes after header:', hexDump);
}
